import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule }  from '@angular/forms';
import { DataService } from '../../services/data.service';

// ──────────────────────────────────────────────────────────
// TYPES & INTERFACES
// ──────────────────────────────────────────────────────────
export type TimetableTab = 'class' | 'teacher' | 'periods' | 'manage';
export type DayOfWeek    = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';

export interface Period {
  id:          number;
  pname:       string;
  start_time:  string;
  end_time:    string;
  is_break:    boolean;
  break_label: string;
  sort_order:  number;
}

export interface TimetableEntry {
  id:            number;
  period_id:     number;
  teacher_id:    number;
  teacher_name:  string;
  class_name:    string;
  class_section: string;
  class_subject: string;
  class_day:     DayOfWeek;
  class_room:    string;
  academic_year: string;
  is_active:     boolean;
}

export interface TeacherProfile {
  id:          number;
  emp_code:    string;
  name:        string;
  designation: string;
  department:  string;
  subjects:    string[];
  color:       string;
}

export interface ConflictInfo {
  type:    'teacher' | 'room';
  message: string;
  entries: number[];
}

@Component({
  selector:    'app-timetable',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './timetable.html',
  styleUrl:    './timetable.scss',
})
export class TimetableComponent implements OnInit {
  dataService     = inject(DataService);
  loading = false;
  user: any = null;

  activeTab       = signal<TimetableTab>('class');
  saveSuccess     = signal('');
  showCellModal   = signal(false);
  showPeriodModal = signal(false);
  editingEntry    = signal<TimetableEntry | null>(null);
  editingPeriod   = signal<Period | null>(null);
  showConflicts   = signal(false);
  isPrintMode     = signal(false);

  // ── Filters (signals so computed views re-run on change) ──
  selectedClass     = signal('');
  selectedSection   = signal('A');
  selectedAcYear    = signal('2026-27');
  selectedTeacherId = signal(0);

  // ── Cell form ─────────────────────────────────────────
  cellForm: Partial<TimetableEntry> = {};
  cellDay: DayOfWeek    = 'Monday';
  cellPeriodId: number  = 0;

  // ── Period form ───────────────────────────────────────
  periodForm: Partial<Period> = {};
  periodSubmitted = false;

  // ── Manage filters ────────────────────────────────────
  manageClass   = signal('');
  manageTeacher = signal(0);
  manageDay     = signal('');
  manageSearch  = signal('');

  // ── LOOKUPS ───────────────────────────────────────────
  readonly DAYS: DayOfWeek[] = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  readonly classOptions = this.dataService.classes;
  readonly sectionOptions = this.dataService.sections;
  readonly academicYears  = this.dataService.fin_period;
  readonly roomOptions = this.dataService.class_rooms;

  colorCodes: any = [
    '#1a3a6b', '#0284c7', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2', '#ea580c',
    '#be185d', '#65a30d', '#4338ca', '#0d9488', '#c2410c', '#7c3aed', '#a21caf', '#0369a1',
    '#15803d', '#6b7280', '#ea580c', '#059669', '#1a3a6b', '#0284c7', '#65a30d', '#4338ca',
    '#696969'
  ];

  // add as {subject-name: colorCode, subject-name: colorCode, subject-name: colorCode etc.}
  subjectColors = signal<Record<string, string>> ({});
  subjectList: any = this.dataService.subjects;

  // ── TEACHERS ──────────────────────────────────────────
  teachers: TeacherProfile[] = [
    // { id:'T001', name:'Dr. Rajesh Kumar',   designation:'Head of Dept', department:'Mathematics',        subjects:['Mathematics'],                             color:'#1a3a6b' },
    // { id:'T002', name:'Mrs. Priya Sharma',  designation:'Senior Teacher',department:'Science',           subjects:['Physics','Physics Lab'],                   color:'#0284c7' },
    // { id:'T003', name:'Mr. Amit Verma',     designation:'Teacher',      department:'English',            subjects:['English'],                                color:'#d97706' },
    // { id:'T004', name:'Mrs. Sunita Singh',  designation:'Teacher',      department:'Science',            subjects:['Chemistry','Chemistry Lab'],               color:'#7c3aed' },
    // { id:'T005', name:'Mr. Vikram Patel',   designation:'Teacher',      department:'Computer Science',   subjects:['Computer Science'],                       color:'#0891b2' },
    // { id:'T006', name:'Mrs. Meena Joshi',   designation:'Teacher',      department:'Hindi',              subjects:['Hindi'],                                  color:'#dc2626' },
    // { id:'T007', name:'Mr. Arjun Chauhan',  designation:'Teacher',      department:'Physical Education', subjects:['Physical Education','Activity/Sports','Yoga/Health'], color:'#ea580c' },
    // { id:'T008', name:'Mrs. Kavita Nair',   designation:'Teacher',      department:'Science',            subjects:['Biology','Biology Lab'],                  color:'#059669' },
    // { id:'T009', name:'Mr. Rahul Mishra',   designation:'Teacher',      department:'Social Science',     subjects:['History','Geography','Social Science'],   color:'#be185d' },
    // { id:'T010', name:'Mrs. Anjali Gupta',  designation:'Teacher',      department:'Commerce',           subjects:['Accountancy','Business Studies','Economics'], color:'#0d9488' },
  ];

  // ── PERIODS ───────────────────────────────────────────
  private nextPeriodId = 9;
  periods = signal<Period[]>([
    // { id:1, pname:'Period 1',  start_time:'07:30', end_time:'08:15', is_break:false, break_label:'', sort_order:1 },
    // { id:2, pname:'Period 2',  start_time:'08:15', end_time:'09:00', is_break:false, break_label:'', sort_order:2 },
    // { id:3, pname:'Period 3',  start_time:'09:00', end_time:'09:45', is_break:false, break_label:'', sort_order:3 },
    // { id:4, pname:'Break',     start_time:'09:45', end_time:'10:00', is_break:true,  break_label:'Morning Break', sort_order:4 },
    // { id:5, pname:'Period 4',  start_time:'10:00', end_time:'10:45', is_break:false, break_label:'', sort_order:5 },
    // { id:6, pname:'Period 5',  start_time:'10:45', end_time:'11:30', is_break:false, break_label:'', sort_order:6 },
    // { id:7, pname:'Lunch',     start_time:'11:30', end_time:'12:00', is_break:true,  break_label:'Lunch Break',  sort_order:7 },
    // { id:8, pname:'Period 6',  start_time:'12:00', end_time:'12:45', is_break:false, break_label:'', sort_order:8 },
    // { id:9, pname:'Period 7',  start_time:'12:45', end_time:'13:30', is_break:false, break_label:'', sort_order:9 },
  ]);

  // ── TIMETABLE ENTRIES ─────────────────────────────────
  private nextEntryId = 50;
  entries = signal<TimetableEntry[]>([]);       //(this.buildSeedEntries());

  // ── COMPUTED ─────────────────────────────────────────
  readonly classEntries = computed(() =>
    this.entries().filter(e =>
      e.class_name    === this.selectedClass() &&
      e.class_section === this.selectedSection() &&
      e.academic_year === this.selectedAcYear() &&
      e.is_active
    )
  );

  readonly teacherEntries = computed(() =>
    this.entries().filter(e =>
      e.teacher_id     === Number(this.selectedTeacherId()) &&
      e.academic_year  === this.selectedAcYear() &&
      e.is_active
    )
  );

  readonly filteredEntries = computed(() => {
    const q       = this.manageSearch().toLowerCase().trim();
    const cls     = this.manageClass();
    const teacher = this.manageTeacher();
    const day     = this.manageDay();
    return this.entries().filter(e =>
      (!cls     || e.class_name === cls) &&
      (!teacher || e.teacher_id === teacher) &&
      (!day     || e.class_day  === day) &&
      (!q || [e.class_subject, e.teacher_name, e.class_room, e.class_name, e.class_section]
        .some(v => v.toLowerCase().includes(q)))
    );
  });

  readonly conflicts = computed(() => {
    const c: ConflictInfo[] = [];
    const active = this.entries().filter(e => e.is_active);
    // Teacher conflict: same teacher, same day, same period, different class
    const tMap = new Map<string, number[]>();
    active.forEach(e => {
      const key = `${e.teacher_id}|${e.class_day}|${e.period_id}`;
      const arr  = tMap.get(key) ?? [];
      arr.push(e.id);
      tMap.set(key, arr);
    });
    tMap.forEach((ids, key) => {
      if (ids.length > 1) {
        const [tid, day, pid] = key.split('|');
        const t = this.teachers.find(t => t.id === parseInt(tid));
        c.push({ type:'teacher', message:`${t?.name ?? tid} is double-booked on ${day} Period ${pid}`, entries: ids });
      }
    });
    // Room conflict: same room, same day, same period, different class
    const rMap = new Map<string, number[]>();
    active.filter(e => e.class_room).forEach(e => {
      const key = `${e.class_room}|${e.class_day}|${e.period_id}`;
      const arr  = rMap.get(key) ?? [];
      arr.push(e.id);
      rMap.set(key, arr);
    });
    rMap.forEach((ids, key) => {
      if (ids.length > 1) {
        const [room, day] = key.split('|');
        c.push({ type:'room', message:`${room} is double-booked on ${day}`, entries: ids });
      }
    });
    return c;
  });

  readonly classStats = computed(() => {
    const totalPeriods = this.DAYS.length * this.periods().filter(p => !p.is_break).length;
    const filled       = new Set(this.classEntries().map(e => `${e.class_day}|${e.period_id}`)).size;
    return { total: totalPeriods, filled, empty: totalPeriods - filled, subjects: new Set(this.classEntries().map(e => e.class_subject)).size };
  });

  readonly teacherStats = computed(() => {
    const e = this.teacherEntries();
    const days = new Set(e.map(x => x.class_day)).size;
    const periodsPerDay = Math.round(e.length / (days || 1));
    return { total: e.length, days, classes: new Set(e.map(x => x.class_name)).size, subjects: new Set(e.map(x => x.class_subject)).size };
  });

  async ngOnInit() {
    let erp_rem: any = localStorage.getItem("erp_rem");
    if(erp_rem && !this.user) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;
    }

    let subjects: any = this.dataService.subjects;
    if(subjects && subjects.length) {
      for(let i=0; i<subjects.length; i++) {
        const subName: any = subjects[i].name;
        this.subjectColors()[subName] = this.colorCodes[i];
      }
    }

    this.loading = true;
    await this.dataService.getAllRecords('periods').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.periods.set(res);
      }
    });
    
    this.loading = true;
    await this.dataService.getAllRecords('tt_assign').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.entries.set(res);
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('staff').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        for(let i=0; i<res.length; i++) {
          const staff: any = res[i];

          this.teachers.push({ 
            id: staff.id,
            emp_code: staff.emp_code,
            name: staff.first_name + ' ' + staff.last_name,
            designation: staff.designation,
            department: staff.department,
            subjects: ['Physics','Physics Lab'],
            color: this.colorCodes[i]
          });
        }
      }
    });

  }

  // ── Cell helpers ──────────────────────────────────────
  getCell(day: DayOfWeek, periodId: number): TimetableEntry | null {
    return this.classEntries().find(e => e.class_day === day && e.period_id === periodId) ?? null;
  }

  getTeacherCell(day: DayOfWeek, periodId: number): TimetableEntry | null {
    return this.teacherEntries().find(e => e.class_day === day && e.period_id === periodId) ?? null;
  }

  getSubjectColor(subject: string): string {
    let mySubjs: any = this.subjectColors();
    const clrCode: string = mySubjs[subject] ?? '#6b7280';
    return clrCode;
  }

  getTeacherName(id: number): string {
    id = Number(this.cellForm.teacher_id);
    return this.teachers.find((t: any) => t.id === id)?.name ?? '-';
  }

  // ── Cell modal ────────────────────────────────────────
  openCell(day: DayOfWeek, periodId: number): void {
    const existing = this.getCell(day, periodId);
    this.cellDay     = day;
    this.cellPeriodId = periodId;
    this.cellForm = existing
      ? { ...existing }
      : {
          class_name:    this.selectedClass(),
          class_section:       this.selectedSection(),
          academic_year: this.selectedAcYear(),
          class_day: day, period_id: periodId,
          class_subject: '', teacher_name: '', teacher_id: 0, class_room: '', is_active: true,
        };
    this.editingEntry.set(existing ?? null);
    this.showCellModal.set(true);
  }

  openTeacherCell(day: DayOfWeek, periodId: number): void {
    const existing = this.getTeacherCell(day, periodId);
    this.cellDay     = day;
    this.cellPeriodId = periodId;
    const teacher    = this.teachers.find(t => t.id === this.selectedTeacherId());
    this.cellForm = existing
      ? { ...existing }
      : {
          class_name: '', class_section: 'A', academic_year: this.selectedAcYear(),
          class_day: day, period_id: periodId,
          class_subject: teacher?.subjects[0] ?? '', teacher_name: teacher?.name ?? '',
          teacher_id: this.selectedTeacherId(), class_room: '', is_active: true,
        };
    this.editingEntry.set(existing ?? null);
    this.showCellModal.set(true);
  }

  onSubjectChange(): void {
    if (!this.cellForm.teacher_id && this.cellForm.class_subject) {
      const t = this.teachers.find(t => t.subjects.includes(this.cellForm.class_subject!));
      if (t) { this.cellForm.teacher_id = t.id; this.cellForm.teacher_name = t.name; }
    }
  }

  onTeacherChange(): void {
    this.cellForm.teacher_id = Number(this.cellForm.teacher_id);
    const t = this.teachers.find(t => t.id === this.cellForm.teacher_id);
    if (t) this.cellForm.teacher_name = t.name;
  }

  saveCell(): void {
    if (!this.cellForm.class_subject) return;
    if (!this.cellForm.teacher_id) return;
    this.cellForm.teacher_id = Number(this.cellForm.teacher_id);

    const teacher = this.teachers.find(t => t.id === this.cellForm.teacher_id);
    const entry: TimetableEntry = {
      id:            this.editingEntry()?.id ?? this.nextEntryId++,
      class_name:    this.cellForm.class_name!,
      class_section: this.cellForm.class_section!,
      academic_year: this.cellForm.academic_year!,
      class_day:     this.cellForm.class_day!,
      period_id:     this.cellForm.period_id!,
      class_subject: this.cellForm.class_subject!,
      teacher_name:  teacher?.name ?? this.cellForm.teacher_name ?? '',
      teacher_id:    this.cellForm.teacher_id!,
      class_room:    this.cellForm.class_room ?? '',
      is_active:     true,
    };

    if (this.editingEntry()) {
      this.doUpdate('tt_assign', entry);
      this.entries.update(list => list.map(e => e.id === entry.id ? entry : e));
    } else {
      this.doSave('tt_assign', entry);
      this.entries.update(list => [...list, entry]);
    }

    this.showCellModal.set(false);
    this.flash('Timetable updated!');
  }

  deleteCell(day: DayOfWeek, periodId: number): void {
    const e = this.getCell(day, periodId);
    if (!e) return;
    if (!confirm(`Remove ${e.class_subject} from ${day} ${this.periods().find(p => p.id === periodId)?.pname}?`)) return;

    // this.doDelete('periods', periodId);
    this.entries.update(list => list.filter(x => x.id !== e.id));
    this.flash('Entry removed.');
  }

  deleteEntry(id: number): void {
    if (!confirm('Remove this timetable entry?')) return;
    this.entries.update(list => list.filter(e => e.id !== id));
    this.flash('Entry removed.');
  }

  // ── Period modal ──────────────────────────────────────
  openPeriodForm(p?: Period): void {
    this.periodForm = p ? { ...p } : { pname:'', start_time:'', end_time:'', is_break:false, break_label:'', sort_order: this.periods().length + 1 };
    this.editingPeriod.set(p ?? null);
    this.periodSubmitted = false;
    this.showPeriodModal.set(true);
  }

  savePeriod(): void {
    this.periodSubmitted = true;
    if (!this.periodForm.pname || !this.periodForm.start_time || !this.periodForm.end_time) return;
    const p: Period = {
      id:          this.editingPeriod()?.id ?? this.nextPeriodId++,
      pname:       this.periodForm.pname!,
      start_time:  this.periodForm.start_time!,
      end_time:    this.periodForm.end_time!,
      is_break:    this.periodForm.is_break ?? false,
      break_label: this.periodForm.break_label ?? '',
      sort_order:  this.periodForm.sort_order ?? this.periods().length + 1,
    };

    if (this.editingPeriod()) {
      this.doUpdate('periods', p);
      this.periods.update(list => list.map(x => x.id === p.id ? p : x).sort((a,b) => a.sort_order - b.sort_order));
    } else {
      this.doSave('periods', p);
      this.periods.update(list => [...list, p].sort((a,b) => a.sort_order - b.sort_order));
    }

    this.showPeriodModal.set(false);
    this.flash('Period saved!');
  }

  deletePeriod(id: number): void {
    if (!confirm('Delete this period? Timetable entries using it will also be removed.')) return;

    this.doDelete('periods', id);
    this.periods.update(list => list.filter(p => p.id !== id));
    this.entries.update(list => list.filter(e => e.period_id !== id));
    this.flash('Period deleted.');
  }

  // ── Copy day ──────────────────────────────────────────
  copyDayTo(fromDay: DayOfWeek, toDay: DayOfWeek): void {
    const fromEntries = this.classEntries().filter(e => e.class_day === fromDay);
    if (!fromEntries.length) { alert(`No entries on ${fromDay} to copy.`); return; }
    if (!confirm(`Copy ${fromDay} timetable to ${toDay}? Existing ${toDay} entries will be replaced.`)) return;
    this.entries.update(list => {
      const filtered = list.filter(e =>
        !(e.class_name === this.selectedClass() && e.class_section === this.selectedSection() && e.class_day === toDay)
      );
      const copied = fromEntries.map(e => ({
        ...e, id: this.nextEntryId++, day: toDay,
      }));
      return [...filtered, ...copied];
    });
    this.flash(`${fromDay} copied to ${toDay}.`);
  }

  // ── Helpers ───────────────────────────────────────────
  getPeriodLabel(id: number): string {
    const p = this.periods().find(p => p.id === id);
    return p ? `${p.pname} (${p.start_time}–${p.end_time})` : `Period ${id}`;
  }

  formatTime(t: string): string {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hour = Number(h);
    const ampm  = hour >= 12 ? 'PM' : 'AM';
    const h12   = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  }

  getTeacherSubjects(id: number): string[] {
    return this.teachers.find(t => t.id === id)?.subjects ?? [];
  }

  isConflictEntry(id: number): boolean {
    return this.conflicts().some(c => c.entries.includes(id));
  }

  print(): void { this.isPrintMode.set(true); setTimeout(() => { window.print(); this.isPrintMode.set(false); }, 300); }

  resetManageFilters(): void {
    this.manageClass.set('');
    this.manageTeacher.set(1);
    this.manageDay.set('');
    this.manageSearch.set('');
  }

  calcPeriodDuration(p: Period): number {
    return Math.round((new Date('1970-01-01T' + p.end_time).getTime() - new Date('1970-01-01T' + p.start_time).getTime()) / 60000);
  }

  private flash(msg: string): void {
    this.saveSuccess.set(msg);
    setTimeout(() => this.saveSuccess.set(''), 2500);
  }

  // ── SEED ENTRIES ──────────────────────────────────────
  /*
  private buildSeedEntries(): TimetableEntry[] {
    const cls = '12c', sec = 'A', yr = '2025-26';
    const data: [DayOfWeek, number, string, string][] = [
      // Mon
      ['Monday',    1,'Mathematics',       'T001'],
      ['Monday',    2,'Physics',           'T002'],
      ['Monday',    3,'Chemistry',         'T004'],
      ['Monday',    5,'English',           'T003'],
      ['Monday',    6,'Biology',           'T008'],
      ['Monday',    8,'Computer Science',  'T005'],
      ['Monday',    9,'Physical Education','T007'],
      // Tue
      ['Tuesday',   1,'Physics',           'T002'],
      ['Tuesday',   2,'Mathematics',       'T001'],
      ['Tuesday',   3,'English',           'T003'],
      ['Tuesday',   5,'Chemistry',         'T004'],
      ['Tuesday',   6,'Mathematics',       'T001'],
      ['Tuesday',   8,'Hindi',             'T006'],
      ['Tuesday',   9,'Biology',           'T008'],
      // Wed
      ['Wednesday', 1,'Chemistry',         'T004'],
      ['Wednesday', 2,'Biology',           'T008'],
      ['Wednesday', 3,'Mathematics',       'T001'],
      ['Wednesday', 5,'Physics Lab',       'T002'],
      ['Wednesday', 6,'Physics Lab',       'T002'],
      ['Wednesday', 8,'English',           'T003'],
      ['Wednesday', 9,'Hindi',             'T006'],
      // Thu
      ['Thursday',  1,'English',           'T003'],
      ['Thursday',  2,'Chemistry',         'T004'],
      ['Thursday',  3,'Biology',           'T008'],
      ['Thursday',  5,'Mathematics',       'T001'],
      ['Thursday',  6,'Chemistry Lab',     'T004'],
      ['Thursday',  8,'Chemistry Lab',     'T004'],
      ['Thursday',  9,'Computer Science',  'T005'],
      // Fri
      ['Friday',    1,'Biology',           'T008'],
      ['Friday',    2,'English',           'T003'],
      ['Friday',    3,'Physics',           'T002'],
      ['Friday',    5,'Hindi',             'T006'],
      ['Friday',    6,'Mathematics',       'T001'],
      ['Friday',    8,'Biology Lab',       'T008'],
      ['Friday',    9,'Biology Lab',       'T008'],
      // Sat
      ['Saturday',  1,'Mathematics',       'T001'],
      ['Saturday',  2,'Hindi',             'T006'],
      ['Saturday',  3,'Physics',           'T002'],
      ['Saturday',  5,'English',           'T003'],
      ['Saturday',  6,'Activity/Sports',   'T007'],
      ['Saturday',  8,'Yoga/Health',       'T007'],
      ['Saturday',  9,'Computer Science',  'T005'],
    ];

    const roomMap: Record<string, string> = {
      'Mathematics':'Room 101','Physics':'Room 102','Chemistry':'Room 103',
      'Biology':'Room 104','English':'Room 105','Hindi':'Room 105',
      'Computer Science':'Computer Lab','Physics Lab':'Lab 1 (Physics)',
      'Chemistry Lab':'Lab 2 (Chemistry)','Biology Lab':'Lab 3 (Biology)',
      'Physical Education':'Gymnasium','Activity/Sports':'Gymnasium','Yoga/Health':'Room 101',
    };

    return data.map(([day, pid, subj, tid], i) => ({
      id: i + 1,
      class_name:    cls,
      class_section:       sec,
      academic_year: yr,
      class_day:  day, 
      period_id: pid, 
      class_subject: subj,
      teacher_name:   this.teachers.find(t => t.id === parseInt(tid))?.name ?? '-',
      teacher_id:  parseInt(tid),
      class_room:  roomMap[subj] ?? 'Room 101',
      is_active: true,
    }));
  }
  */

  // ── ACTIONS ───────────────────────────────────────────
  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

}
